/**
 * The outfit economy in Gyro Admin (gyro-svc desktopMascotEconomy.ts, desktopMascotEconomyRules.ts,
 * desktopMascotEconomyRoutes.ts; docs/STRATEGY-outfit-economy.md; owner, 2026-10-15), without React: every outfit is a
 * collectible item with a rarity (a kyber crystal: white, emerald, orange, purple) and a supply, unlimited or a fixed
 * number of copies, and IT sets both here. This file holds:
 *  - the rules, copied from services/gyro-svc/src/desktopMascotEconomyRules.ts and cross-tested against it
 *    (apps/admin/test/outfit-economy.test.ts): the bands each rarity proposes, a cap never below the copies given out
 *    nor raised after the first, the status changes, sold out, the keep rule;
 *  - the service's view and body shapes (lib/api.ts);
 *  - the item form and its refusals, mirrored from the service so a save is refused here first with the same sentence;
 *  - the section's wording (product words: outfits, mascot, Common / Uncommon / Rare / Legendary, kyber crystal, Sold out).
 * The console is advisory: the service judges every save again.
 */
import { ApiError } from './api';

// ---- The rules (cross-tested against the service) ----

export const RARITIES = ['common', 'uncommon', 'rare', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];
export const ITEM_KINDS = ['outfit', 'companion', 'keep'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];
export const ITEM_STATUSES = ['active', 'retired', 'withdrawn'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];
export type KyberCrystal = 'white' | 'emerald' | 'orange' | 'purple';
export type Supply = { kind: 'unlimited' } | { kind: 'capped'; cap: number };

/** The crystal of each rarity (its colour is a token in ui/tokens.css, `--ui-kyber-*`, never a literal here). */
export const KYBER_CRYSTALS: Readonly<Record<Rarity, KyberCrystal>> = Object.freeze({ common: 'white', uncommon: 'emerald', rare: 'orange', legendary: 'purple' });
export const DEFAULT_SUPPLY_BANDS: Readonly<Record<Rarity, number | null>> = Object.freeze({ common: null, uncommon: 200, rare: 50, legendary: 10 });
export const DEFAULT_PRICE_BANDS: Readonly<Record<Rarity, number>> = Object.freeze({ common: 20, uncommon: 60, rare: 200, legendary: 750 });
export const ANIMATIONS_MINIMUM: Readonly<Record<Rarity, 0 | 1 | 2 | 3>> = Object.freeze({ common: 0, uncommon: 1, rare: 2, legendary: 3 });
export const RARITY_LABELS: Readonly<Record<Rarity, string>> = Object.freeze({ common: 'Common', uncommon: 'Uncommon', rare: 'Rare', legendary: 'Legendary' });
export const SUPPLY_CAP_MAX = 100_000;
export const ECONOMY_NAME_MAX = 40;
export const PACK_ITEMS_MAX = 50;
export const OUTFIT_ITEM_ID_RE = /^o-[a-z0-9]{10}$/;
export const KEEP_ITEM_ID_RE = /^k-[a-z0-9]{10}$/;
export const ITEM_ID_RE = /^[ock]-[a-z0-9]{10}$/;
export const COLLECTION_ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
export const PACK_ID_RE = /^p-[a-z0-9]{10}$/;
export const GREETING_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
/** The kyber crystal's one path (packages/gyro-react/src/kyberCrystal.tsx KYBER_CRYSTAL_PATH; cross-tested). */
export const KYBER_CRYSTAL_PATH = 'M12 1.5 18.5 8 12 22.5 5.5 8 12 1.5Zm0 3.2L8.3 8.4 12 18.6l3.7-10.2L12 4.7Z';

export function proposedSupply(rarity: Rarity): Supply {
  const cap = DEFAULT_SUPPLY_BANDS[rarity];
  return cap === null ? { kind: 'unlimited' } : { kind: 'capped', cap };
}

export function proposedPrice(rarity: Rarity): number {
  return DEFAULT_PRICE_BANDS[rarity];
}

export function crystalOf(rarity: Rarity): KyberCrystal {
  return KYBER_CRYSTALS[rarity];
}

export function defaultTradeable(supply: Supply): boolean {
  return supply.kind === 'capped';
}

export type CapRefusal = 'cap_below_minted' | 'cap_raised_after_mint';

export function checkSupplyChange(current: Supply, next: Supply, minted: number): CapRefusal | null {
  if (next.kind === 'capped' && next.cap < minted) return 'cap_below_minted';
  if (minted === 0) return null;
  if (current.kind === 'capped') {
    if (next.kind === 'unlimited' || next.cap > current.cap) return 'cap_raised_after_mint';
  }
  return null;
}

export function isSoldOut(supply: Supply, minted: number): boolean {
  return supply.kind === 'capped' && minted >= supply.cap;
}

export function remainingCopies(supply: Supply, minted: number): number | null {
  return supply.kind === 'capped' ? Math.max(0, supply.cap - minted) : null;
}

export function statusChangeAllowed(from: ItemStatus, to: ItemStatus): boolean {
  if (from === to) return true;
  return from !== 'withdrawn';
}

export function animationsGap(rarity: Rarity, bound: number): { minimum: number; bound: number; missing: number } {
  const minimum = ANIMATIONS_MINIMUM[rarity];
  return { minimum, bound, missing: Math.max(0, minimum - bound) };
}

export type KeepTargetWindow = 'always' | 'between' | 'yearly' | 'day';

export function keepTargetRefusal(target: { window: KeepTargetWindow } | undefined): 'keep_target_unknown' | 'keep_target_always' | null {
  if (!target) return 'keep_target_unknown';
  return target.window === 'always' ? 'keep_target_always' : null;
}

export function serialText(serial: number | undefined, supply: Supply | 'unlimited' | { cap: number; minted: number }): string {
  if (serial === undefined) return '';
  if (supply === 'unlimited' || ('kind' in supply && supply.kind === 'unlimited')) return 'unlimited';
  return `#${serial} of ${(supply as { cap: number }).cap}`;
}

// ---- The service's shapes ----

export interface EconomyItem {
  kind: ItemKind;
  of?: string;
  rarity: Rarity;
  supply: Supply;
  status: ItemStatus;
  tradeable: boolean;
  collectionId?: string;
  animations: { minimum: 0 | 1 | 2 | 3 };
  greetingId?: string;
  updatedBy: string;
  updatedAtMs: number;
}

export interface EconomyCollection {
  id: string;
  name: string;
  order: string[];
  updatedBy: string;
  updatedAtMs: number;
}

export interface EconomyPack {
  id: string;
  name: string;
  collectionId: string;
  items: string[];
  supply: Supply;
  priceHint: number | null;
  status: ItemStatus;
  updatedBy: string;
  updatedAtMs: number;
}

export interface EconomyHistoryLine {
  action: 'item' | 'collection' | 'pack';
  id: string;
  detail: string;
  by: string;
  atMs: number;
}

export interface SupplyEntry {
  minted: number;
  cap: number | null;
  remaining: number | null;
  soldOut: boolean;
  soldOutAtMs: number | null;
}

/** GET /v1/admin/desktop/mascot/economy. */
export interface EconomyView {
  enabled: true;
  revision: number;
  items: Record<string, EconomyItem>;
  collections: Record<string, EconomyCollection>;
  packs: Record<string, EconomyPack>;
  supply: Record<string, SupplyEntry>;
  /** Eligible action packs bound per outfit id (and 'default'); null while mascot animations are off or unreadable. */
  animations: Record<string, number> | null;
  /** Newest first. */
  history: EconomyHistoryLine[];
  bands: { supply: Record<Rarity, number | null>; price: Record<Rarity, number>; animations: Record<Rarity, number>; crystals: Record<Rarity, { crystal: KyberCrystal; color: string }> };
  /** On a save whose audit record is still waiting for the ledger. */
  auditPending?: true;
  message?: string;
}

export interface SupplyRow {
  itemId: string;
  kind: ItemKind;
  rarity: Rarity;
  crystal: KyberCrystal;
  status: ItemStatus;
  cap: number;
  minted: number;
  remaining: number;
  soldOut: boolean;
  soldOutAtMs: number | null;
  holders: number;
  collectionId: string | null;
}

export interface SupplyReport {
  rows: SupplyRow[];
}

export interface SaveEconomyItemBody {
  expectedRevision: number;
  kind: ItemKind;
  of?: string;
  rarity: Rarity;
  supply: Supply;
  status: ItemStatus;
  tradeable: boolean;
  collectionId?: string | null;
  animations?: { minimum: 0 | 1 | 2 | 3 };
  greetingId?: string | null;
}

export interface SaveEconomyCollectionBody {
  expectedRevision: number;
  name: string;
  order?: string[];
}

export interface SaveEconomyPackBody {
  expectedRevision: number;
  name: string;
  collectionId: string;
  items: string[];
  supply: Supply;
  priceHint?: number | null;
  status: ItemStatus;
}

// ---- The item form ----

export interface ItemForm {
  kind: 'outfit' | 'keep';
  /** A keep item: the outfit it keeps. */
  of: string;
  rarity: Rarity;
  supplyKind: 'unlimited' | 'capped';
  /** The cap as typed, so a half-typed number is never thrown away. */
  cap: string;
  status: ItemStatus;
  tradeable: boolean;
  collectionId: string;
  animations: 0 | 1 | 2 | 3;
  greetingId: string;
}

/** The form for an item: its record, or what its rarity proposes for an outfit without one. */
export function itemFormOf(item: EconomyItem | undefined, kind: 'outfit' | 'keep' = 'outfit', rarity: Rarity = 'common'): ItemForm {
  if (!item) {
    const supply = proposedSupply(rarity);
    return { kind, of: '', rarity, supplyKind: supply.kind, cap: supply.kind === 'capped' ? String(supply.cap) : '', status: 'active', tradeable: defaultTradeable(supply), collectionId: '', animations: ANIMATIONS_MINIMUM[rarity], greetingId: '' };
  }
  return {
    kind: item.kind === 'keep' ? 'keep' : 'outfit',
    of: item.of ?? '',
    rarity: item.rarity,
    supplyKind: item.supply.kind,
    cap: item.supply.kind === 'capped' ? String(item.supply.cap) : '',
    status: item.status,
    tradeable: item.tradeable,
    collectionId: item.collectionId ?? '',
    animations: item.animations.minimum,
    greetingId: item.greetingId ?? '',
  };
}

/** The form with another rarity: an item not yet saved, or never given out, takes what the rarity proposes. */
export function withRarity(form: ItemForm, rarity: Rarity, propose: boolean): ItemForm {
  if (!propose) return { ...form, rarity };
  const supply = proposedSupply(rarity);
  return { ...form, rarity, supplyKind: supply.kind, cap: supply.kind === 'capped' ? String(supply.cap) : '', tradeable: defaultTradeable(supply), animations: ANIMATIONS_MINIMUM[rarity] };
}

export function supplyOf(form: Pick<ItemForm, 'supplyKind' | 'cap'>): Supply | null {
  if (form.supplyKind === 'unlimited') return { kind: 'unlimited' };
  const cap = Number(form.cap.trim());
  if (!/^\d+$/.test(form.cap.trim()) || !Number.isSafeInteger(cap) || cap < 1 || cap > SUPPLY_CAP_MAX) return null;
  return { kind: 'capped', cap };
}

export const CAP_REFUSAL_TEXT: Readonly<Record<CapRefusal, string>> = Object.freeze({
  cap_below_minted: 'The cap cannot go below the copies already given out. Nothing was changed.',
  cap_raised_after_mint: 'Once a copy has been given out, the cap can only come down: a collector who was given "#3 of 10" was promised ten. Nothing was changed.',
});

/**
 * Why the form cannot be saved as it is, with the service's own sentence, or null: the cap as a number, the cap rules
 * against the copies given out, a keep item's outfit, the greeting id, a withdrawn item.
 */
export function itemFormRefusal(form: ItemForm, current: EconomyItem | undefined, minted: number): string | null {
  const supply = supplyOf(form);
  if (!supply) return `A cap is a whole number from 1 to ${SUPPLY_CAP_MAX.toLocaleString('en-US')}.`;
  const cap = checkSupplyChange(current?.supply ?? { kind: 'unlimited' }, supply, minted);
  if (cap) return CAP_REFUSAL_TEXT[cap];
  if (current && !statusChangeAllowed(current.status, form.status)) return 'A withdrawn item stays withdrawn. Nothing was changed.';
  if (form.kind === 'keep' && !OUTFIT_ITEM_ID_RE.test(form.of)) return 'A keep item names the outfit it keeps.';
  if (form.greetingId.trim() && !GREETING_ID_RE.test(form.greetingId.trim())) return 'A greeting id is lower-case letters, digits and dashes.';
  return null;
}

export function itemBody(form: ItemForm, expectedRevision: number): SaveEconomyItemBody {
  const supply = supplyOf(form) ?? { kind: 'unlimited' as const };
  return {
    expectedRevision,
    kind: form.kind,
    ...(form.kind === 'keep' ? { of: form.of } : {}),
    rarity: form.rarity,
    supply,
    status: form.status,
    tradeable: form.tradeable,
    collectionId: form.collectionId || null,
    animations: { minimum: form.animations },
    greetingId: form.greetingId.trim() || null,
  };
}

// ---- Words ----

export const ECONOMY_SAVED_TEXT = 'Saved.';
export const ECONOMY_CHANGED_TEXT = 'Another admin changed the outfit economy after you loaded it, so nothing was saved. The economy was reloaded; make your change again.';
export const ECONOMY_UNREADABLE_TEXT = 'The outfit economy could not be read just now.';
export const ECONOMY_HINT = 'Every outfit is a collectible: a rarity, shown as a kyber crystal (white Common, emerald Uncommon, orange Rare, purple Legendary), and a supply, unlimited or a fixed number of copies. A grant of a capped outfit gives the next copy ("#7 of 50") and is refused once every copy is out. A cap never goes below the copies given out, nor up after the first. Prices live in Kyber’s catalog in the Hub; the bands here are what the console proposes.';

/** "Rare · orange kyber crystal": the rarity picker's option. */
export function rarityOptionText(rarity: Rarity): string {
  return `${RARITY_LABELS[rarity]} · ${crystalOf(rarity)} kyber crystal`;
}

/** "Rarity proposes 50 copies and a store price of 200 Kyber credits (the admin may override the copies)." */
export function bandHintText(rarity: Rarity): string {
  const cap = DEFAULT_SUPPLY_BANDS[rarity];
  return `${RARITY_LABELS[rarity]} proposes ${cap === null ? 'unlimited copies' : `${cap.toLocaleString('en-US')} copies`} and a store price of ${proposedPrice(rarity).toLocaleString('en-US')} Kyber credits.`;
}

/** The counter in words: "12 of 50 given out, 38 left", "Sold out since Oct 16, 2026 (50 of 50)", "Unlimited; 3 given out". */
export function supplyStatusText(entry: SupplyEntry | undefined, supply: Supply, locale?: string, timeZone?: string): string {
  const minted = entry?.minted ?? 0;
  if (supply.kind === 'unlimited') return `Unlimited; ${minted.toLocaleString('en-US')} given out`;
  if (minted >= supply.cap) {
    const since = entry?.soldOutAtMs ? ` since ${new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone }).format(entry.soldOutAtMs)}` : '';
    return `Sold out${since} (${supply.cap.toLocaleString('en-US')} of ${supply.cap.toLocaleString('en-US')})`;
  }
  return `${minted.toLocaleString('en-US')} of ${supply.cap.toLocaleString('en-US')} given out, ${(supply.cap - minted).toLocaleString('en-US')} left`;
}

/** "Legendary needs 3 packs, 1 bound", "Rare needs 2 packs, 2 bound: ready", "Common needs no animations"; null while the counts are unknown. */
export function animationsGapText(rarity: Rarity, bound: number | null): string | null {
  const minimum = ANIMATIONS_MINIMUM[rarity];
  if (minimum === 0) return `${RARITY_LABELS[rarity]} needs no animations`;
  if (bound === null) return null;
  const gap = animationsGap(rarity, bound);
  return `${RARITY_LABELS[rarity]} needs ${minimum} pack${minimum === 1 ? '' : 's'}, ${bound} bound${gap.missing === 0 ? ': ready' : ` (${gap.missing} to go)`}`;
}

export function supplyText(supply: Supply): string {
  return supply.kind === 'unlimited' ? 'Unlimited' : `${supply.cap.toLocaleString('en-US')} copies`;
}

/** The service refused the revision: another admin changed the economy meanwhile (409 economy_changed). */
export function economyChanged(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.body?.error === 'economy_changed';
}

/** The section's words for a refusal the service sent, else the error's own. */
export function economyRefusalText(error: unknown, fallback: string): string {
  if (economyChanged(error)) return ECONOMY_CHANGED_TEXT;
  if (error instanceof ApiError && typeof error.body?.message === 'string') return error.body.message;
  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

/** A history line as the panel lists it. */
export function historyLineText(line: EconomyHistoryLine, locale?: string, timeZone?: string): string {
  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(line.atMs);
  return `${when} · ${line.by} · ${line.action} ${line.id}: ${line.detail}`;
}
